import { DriverInformation, DriverInformationList, DriverInformationWithMvr, DriverInformationListWithMvr, DriverParty, VehicleInformation, VehicleInformationList, DriverAddress, DriverDetailRequest, ViolationOrderApiResponse, ViolationOrderSimpleValue, OrderMvrResponseList, DriverValidationError, Violation, Action, Accident, MvrApiResponseUnion, MvrApiDataItem, MvrApiDriversLicense, MvrApiDriverHistoryRecord, NormalizedMvrRecord, MvrActivity, AlertMessages, MvrApiMotorVehicleReport, OrderMvrResponseBody } from "../models/DriverInformationModels";
type ViolationResult = ViolationOrderSimpleValue | { data: ViolationOrderSimpleValue[] };

const NOT_AVAILABLE = 'N/A';

export function assignDriverUnits(
	driverList: DriverInformationList,
	vehicleList: VehicleInformationList
): DriverInformationList {
	driverList.drivers.forEach(driver => {
		if (driver?.insuredAssetNumber && driver.insuredAssetNumber !== 0) {
			const match = vehicleList.vehicles.find(
				(v: VehicleInformation) => v.insuredAssetNumber === driver.insuredAssetNumber
			);
			if (match) {
				const unitString = `${match.insuredAssetNumber}) ${match.year} ${match.vehicleDescription}, ${match.classCode}`;
				driver.assignedUnit = unitString;
				driver.year = match.year;
				driver.vehicleDescription = match.vehicleDescription;
				driver.classCode = match.classCode;
				driver.vin = match.vin;
			}
		}
	});

	driverList.drivers.forEach(driver => {
		if (driver.assignedUnit === undefined) {
			driver.assignedUnit = NOT_AVAILABLE;
		}
	});

	return driverList;
}

export function hasDuplicateDriverRecord(driverList: DriverInformationList): boolean {
	return driverList.drivers.some(driver =>
		driver.licenseNumber.trim() === "" &&
		driver.licenseState.trim() === "" &&
		driver.assignedUnit !== NOT_AVAILABLE
	);
}

/**
 * Filters out duplicate drivers where LastName > 16 chars and assignedUnit is "N/A",
 * but another driver exists with the same first 16 chars of LastName and assignedUnit !== "N/A".
 * The assignedUnit from the 2nd match is copied to the 1st match before removal.
 */
export function filterDriversByLastNameThreshold(
	driverList: DriverInformationList,
	lengthThreshold: number
): DriverInformationList {
	const drivers = [...driverList.drivers];
	const toRemoveIndices = new Set<number>();

	for (let i = 0; i < drivers.length; i++) {
		const driver = drivers[i];

		if (
			driver.lastName.length > lengthThreshold &&
			driver.assignedUnit === NOT_AVAILABLE
		) {
			const trimmedLastName = driver.lastName.substring(0, lengthThreshold);

			for (let j = 0; j < drivers.length; j++) {
				if (i === j || toRemoveIndices.has(j)) continue;

				const other = drivers[j];
				if (
					other.lastName.substring(0, lengthThreshold) === trimmedLastName &&
					other.assignedUnit !== NOT_AVAILABLE &&
					other.firstName === driver.firstName &&
					other.dateOfBirth === driver.dateOfBirth
				) {
					driver.assignedUnit = other.assignedUnit;
					toRemoveIndices.add(j);
					if (j < i) i--;
					break;
				}
			}
		}
	}

	const filtered = drivers.filter((_, index) => !toRemoveIndices.has(index));
	return { drivers: filtered };
}


export function mapDriverPartyToDriverInfo(party: DriverParty): DriverInformation {
	return new DriverInformation({
		driverId: String(party.driverId ?? ''),
		firstName: party.firstName ?? '',
		middleInitial: party.middleInitial ?? '',
		lastName: party.lastName ?? '',
		dateOfBirth: party.dateOfBirth ?? '',
		licenseNumber: party.licenseNumber ?? '',
		licenseState: party.licenseState ?? '',
		insuredAssetNumber: Number(party.insuredAssetNumber ?? 0),
		excludedCode: party.exludedCode ?? '',
		assignedUnit: undefined,
		year: undefined,
		classCode: undefined,
		vehicleDescription: undefined,
		genderCode: party.genderCode ?? '',
		maritalStatusCode: party.maritalStatusCode ?? '',
		violationPrediction: party.violationPrediction ?? null,
		selected: false,
		motorVehicleReport: party.motorVehicleReport
	});
}

export function generateOrderRequestData(
	driversList: DriverInformationList,
	driverAddress: DriverAddress
): DriverDetailRequest[] {
	return driversList.drivers
		.filter(driver => driver.violationPrediction == null && driver.selected === true)
		.map(driver => ({
			driverId: driver.driverId,
			license: {
				number: driver.licenseNumber,
				state: driver.licenseState
			},
			name: {
				first: driver.firstName,
				last: driver.lastName,
				...filterOptional({
					middle: driver.middleInitial
				})
			},
			address: {
				street: driverAddress.addressLine,
				zipCode: normalizeZipCode(driverAddress.zipCode)
			},
			biometrics: {
				birthDate: driver.dateOfBirth,
				...filterOptional({
					gender: driver.genderCode,
					maritalStatus: driver.maritalStatusCode
				})
			},
			vehicle: {
				...filterOptional({
					vin: driver.vin != null ? String(driver.vin) : '',
				})
			}
		}));
}

export function updateDriversWithViolationPrediction(
	apiResponse: ViolationOrderApiResponse,
	driversList: DriverInformationList
): DriverInformationList {
	const rawResult = apiResponse.result as { data: ViolationOrderSimpleValue[] };

	const results: ViolationOrderSimpleValue[] = isWrappedResult(rawResult)
		? rawResult.data
		: [];

	const updatedDrivers = driversList.drivers.map(driver => {
		const match = results.find(r => r.licenseNumber === driver.licenseNumber);
		if (match) {
			const orderMvr = match.violationPrediction?.orderMvr;
			if (orderMvr === 'E') {
				driver.violationPrediction = null;
			} else {
				driver.violationPrediction = match.violationPrediction;
				// When violationPrediction is not null, uncheck and disable the checkbox
				if (driver.violationPrediction != null) {
					driver.selected = false;
				}
			}
		}
		return driver;
	});

	return { drivers: updatedDrivers };
}

/**
 * Updates drivers with MVR response data for UI display with proper type safety
 * @param mvrResponseData - The mapped MVR response data
 * @param driversList - The current drivers list
 * @returns Updated drivers list with MVR data and explicit typing
 */
export function updateDriversWithMvrData(
	mvrResponseData: OrderMvrResponseList,
	driversList: DriverInformationList
): DriverInformationListWithMvr {
	const updatedDrivers: DriverInformationWithMvr[] = driversList.drivers.map(driver => {
		// Find matching MVR response for this driver by license number and state
		const matchingMvrResponse = mvrResponseData.find(mvrData => 
			driver.licenseNumber === mvrData.driverInfo.licenseNumber && 
			driver.licenseState === mvrData.driverInfo.licenseState
		);
		
		if (matchingMvrResponse) {
			// Type-safe extension with explicit interface
			return {
				...driver,
				productProcessingStatusDescription: matchingMvrResponse.productProcessingStatusDescription,
				dateTimeCompleted: matchingMvrResponse.dateTimeCompleted,
				orderId: matchingMvrResponse.orderId,
				licenceStatus: matchingMvrResponse.licenceStatus,
				mvrActivity: matchingMvrResponse.mvrActivity,
				alertMessages: matchingMvrResponse.alertMessages,
				hasMvrData: true
			} satisfies DriverInformationWithMvr;
		}
		
		// Driver without new MVR data - preserve existing MVR data if it exists
		const existingDriver = driver as DriverInformationWithMvr; // Cast to access potential MVR properties
		const hasExistingMvrData = existingDriver.hasMvrData || driver.motorVehicleReport;
		
		if (hasExistingMvrData) {
			// Preserve existing MVR properties
			return {
				...driver,
				productProcessingStatusDescription: existingDriver.productProcessingStatusDescription,
				dateTimeCompleted: existingDriver.dateTimeCompleted,
				orderId: existingDriver.orderId,
				licenceStatus: existingDriver.licenceStatus,
				mvrActivity: existingDriver.mvrActivity,
				alertMessages: existingDriver.alertMessages,
				hasMvrData: true
			} satisfies DriverInformationWithMvr;
		} else {
			// Driver without any MVR data - set all MVR properties to undefined
			return {
				...driver,
				productProcessingStatusDescription: undefined as string | undefined,
				dateTimeCompleted: undefined as string | undefined,
				orderId: undefined as string | undefined,
				licenceStatus: undefined as string | undefined,
				mvrActivity: undefined as MvrActivity | undefined,
				alertMessages: undefined as AlertMessages | undefined,
				hasMvrData: false
			} satisfies DriverInformationWithMvr;
		}
	});

	return { drivers: updatedDrivers };
}

function isWrappedResult(obj: ViolationResult): obj is { data: ViolationOrderSimpleValue[] } {
	return 'data' in obj && Array.isArray(obj.data);
}

function normalizeZipCode(zip: string): string {
	const digits = (zip || '').replace(/\D/g, '');
	if (/^\d{5}$/.test(digits)) return digits;
	if (digits.length >= 9) return digits.substring(0, 9);
	if (digits.length >= 5) return digits.substring(0, 5);
	return digits; // fallback, may be empty or incomplete
}

// Utility that removes empty string values from optional fields
function filterOptional<T extends Record<string, unknown>>(obj: T): Partial<T> {
	return Object.fromEntries(
		Object.entries(obj).filter(([, v]) => v != null && v !== '')
	) as Partial<T>;
}

export function validateDriverDetailRequests(
	requests: DriverDetailRequest[]
): DriverValidationError[] {
	const errors: DriverValidationError[] = [];
	const today = new Date().toISOString().split('T')[0];

	const alphaDashSpace = /^[a-zA-Z '\-.]{1,20}$/;
	const licenseRegex = /^[a-zA-Z0-9-*]{1,22}$/;
	const stateRegex = /^[a-zA-Z]{2}$/;
	const streetRegex = /^[a-zA-Z0-9\][! #$%&"'()*+,\-.=?@^_`{|}~]{1,60}$/;
	const zipRegex = /^(\d{5}|\d{9})$/;
	const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

	for (const request of requests) {
		const { name, license, address, biometrics, driverId } = request;
		const driverErrors: string[] = [];

		// License
		if (!licenseRegex.test(license.number)) {
			driverErrors.push(`license number "${license.number}" is invalid.`);
		}
		if (!stateRegex.test(license.state)) {
			driverErrors.push(`license state "${license.state}" must be two letters.`);
		}

		// Name
		if (!alphaDashSpace.test(name.first)) {
			driverErrors.push(`first name "${name.first}" is invalid.`);
		}
		if (!alphaDashSpace.test(name.last)) {
			driverErrors.push(`last name "${name.last}" is invalid.`);
		}
		if (name.middle && !/^[a-zA-Z '\-.]{1,15}$/.test(name.middle)) {
			driverErrors.push(`middle initial "${name.middle}" is invalid.`);
		}

		// Address
		if (!streetRegex.test(address.street)) {
			driverErrors.push(`street "${address.street}" is invalid.`);
		}
		if (!zipRegex.test(address.zipCode)) {
			driverErrors.push(`zip code "${address.zipCode}" must be 5 or 9 digits.`);
		}

		// Birthdate
		if (!dateRegex.test(biometrics.birthDate)) {
			driverErrors.push(`birthDate "${biometrics.birthDate}" is not a valid ISO date.`);
		} else if (biometrics.birthDate > today) {
			driverErrors.push(`birthDate "${biometrics.birthDate}" cannot be in the future.`);
		}

		// Optional fields
		if (biometrics.gender && !/^[a-zA-Z]$/.test(biometrics.gender)) {
			driverErrors.push(`gender "${biometrics.gender}" must be one letter.`);
		}
		if (biometrics.maritalStatus && !/^[a-zA-Z]$/.test(biometrics.maritalStatus)) {
			driverErrors.push(`maritalStatus "${biometrics.maritalStatus}" must be one letter.`);
		}

		if (request.vehicle?.vin && !/^[a-zA-Z0-9]{1,22}$/.test(request.vehicle.vin)) {
			driverErrors.push(`VIN "${request.vehicle.vin}" is invalid.`);
		}

		if (driverErrors.length > 0) {
			errors.push({
				driverId,
				messages: driverErrors
			});
		}
	}

        return errors;
}

export interface DriversLicenseValidationInput {
        state: string;
        licenseNumber?: string;
        firstName?: string;
        middleInitial?: string;
        lastName?: string;
        dateOfBirth?: string;
}

export function validateDriversLicense(info: DriversLicenseValidationInput): string[] {
        const errors: string[] = [];
        const state = info.state?.toUpperCase();
        const license = (info.licenseNumber ?? '').trim();
        const hasDob = Boolean(info.dateOfBirth);
        const hasFullNameWithMiddle = Boolean(info.firstName && info.lastName && info.middleInitial);
        const hasFirstAndLast = Boolean(info.firstName && info.lastName);

        switch (state) {
                case 'AZ': {
                        const validFormat = /^[A-Za-z][0-9]{8}$/.test(license) || /^[A-Za-z]+$/.test(license);
                        if (!validFormat) {
                                errors.push('AZ license must be 1 letter followed by 8 digits or letters only.');
                        }
                        if (!(hasDob || hasFullNameWithMiddle)) {
                                errors.push('AZ requires DLN with DOB or full name.');
                        }
                        break;
                }
                case 'ID': {
                        const validFormat = /^[0-9]{9}$/.test(license) || /^[A-Za-z]{2}[0-9]{6}[A-Za-z]$/.test(license);
                        if (!validFormat) {
                                errors.push('ID license format is invalid.');
                        }
                        if (!hasDob) {
                                errors.push('ID requires DLN and DOB.');
                        }
                        break;
                }
                case 'MT': {
                        const patterns = [
                                /^[0-9]{9}$/,
                                /^[A-Za-z][0-9][A-Za-z0-9][0-9]{2}[A-Za-z\- ]{3}[0-9]$/,
                                /^[0-9]{13}$/,
                                /^[A-Za-z0-9]{3}[0-9]{10}$/
                        ];
                        const validFormat = patterns.some(p => p.test(license));
                        if (!validFormat) {
                                errors.push('MT license format is invalid.');
                        }
                        if (!(hasFirstAndLast && hasDob)) {
                                errors.push('MT requires DLN with full name and DOB.');
                        }
                        break;
                }
                case 'OR': {
                        const validFormat = /^[0-9]{1,9}$/.test(license) || /^[A-Za-z][0-9]{6}$/.test(license);
                        if (!validFormat) {
                                errors.push('OR license format is invalid.');
                        }
                        if (!hasDob) {
                                errors.push('OR requires DLN and DOB.');
                        }
                        break;
                }
                case 'UT': {
                        const validFormat = /^[0-9]{4,9}$/.test(license) || /^0[0-9]{9}$/.test(license);
                        if (!validFormat) {
                                errors.push('UT license format is invalid.');
                        }
                        break;
                }
                case 'WA': {
                        if (!validateWaLicense(license)) {
                                errors.push('WA license format is invalid.');
                        }
                        break;
                }
                case 'WY': {
                        const hasLicense = license.length > 0;
                        const licenseValid = /^[0-9]{9}$/.test(license) || /^[0-9]{6}-[0-9]{3}$/.test(license);
                        if (hasLicense) {
                                if (!licenseValid) {
                                        errors.push('WY license format is invalid.');
                                }
                        } else if (!(hasDob && hasFullNameWithMiddle)) {
                                errors.push('WY requires DLN or full name with DOB.');
                        }
                        break;
                }
                default:
                        errors.push('Unsupported state.');
        }

        return errors;
}

function validateWaLicense(license: string): boolean {
    const dln = license.toUpperCase();
    
    // WDL format: WDL + 4 letters (no AEIOUQV) + 2 alphanumeric (no vowels if letter) + 1 digit + 2 alphanumeric (no vowels if letter)
    const wdlPattern = /^WDL[BCDFGHJKLMNPRSTWXYZ]{4}[BCDFGHJKLMNPRSTWXYZ0-9]{2}[0-9][BCDFGHJKLMNPRSTWXYZ0-9]{2}$/;
    if (wdlPattern.test(dln)) {
        return true;
    }
    
    // Standard format: 1 letter + 6 alphanumeric/asterisk + 2 alphanumeric + 1 digit + 1 letter/digit (1-9) + 1 alphanumeric (not O)
    const standardPattern = /^[A-Z][A-Z0-9*]{6}[A-Z0-9]{2}[0-9][A-Z1-9][A-Z0-9]$/;
    if (dln.length === 12 && standardPattern.test(dln)) {
        // Check last character is not 'O'
        return dln[11] !== 'O';
    }
    
    return false;
}

// Chargeable case status indicators
const CHARGEABLE_TYPES = new Set(
	[
    'ChargeableIf3PlusSameViolationWithin12Months',
    'ChargeableOnlyWithOtherPermissibleViolation',
    'FourPointViolation',
    'OutOfStateViolation',
    'RDCombinationViolation'
	]
)

export function getCaseStatusDisplayText(caseStatusIndicator: string): string {
    return CHARGEABLE_TYPES.has(caseStatusIndicator) ? 'Yes' : 'No'
}

export function getChargeableChipColor(caseStatusIndicator: string): string {
    return CHARGEABLE_TYPES.has(caseStatusIndicator) ? 'error' : 'success'
}

export function getGenderText(code?: string): string {
    switch (code?.toUpperCase()) {
        case 'M': return 'Male'
        case 'F': return 'Female'
        default: return code || '—'
    }
}

/**
 * Normalizes date fields in MVR records for consistent access
 * @param record - The raw MVR record from API
 * @returns Record with normalized date fields
 */
function normalizeRecordDates(record: MvrApiDriverHistoryRecord): MvrApiDriverHistoryRecord & NormalizedMvrRecord {
    // Determine the primary date: violationDate, effectiveDate, or accidentDate
    const primaryDate = record.violationDate ?? record.effectiveDate ?? record.accidentDate ?? record.actionDate ?? '';

    // Determine the conviction/action date: convictionDate, actionEndDate, or reinstatementDate
    const normalizedConvictionDate = record.convictionDate ?? record.actionEndDate ?? record.reinstatementDate ?? record.standardViolation?.convictionDate ?? '';

    return {
        ...record,
        // Add normalized date fields for easier access
        primaryDate,
        normalizedConvictionDate
    };
}

/**
 * Maps MVR API response structure to OrderMvrResponseBody format
 * @param apiResponse - The MVR API response structure (supports both ViolationOrderApiResponse and MvrOrderApiResponse)
 * @returns Mapped OrderMvrResponseBody object
 */
export function mapMvrResponseData(apiResponse: MvrApiResponseUnion): OrderMvrResponseList {
    // Navigate through all MVR records in the nested structure
    const dataArray = apiResponse?.result?.data || [];
    
    // Cast to the expected type since we know this function expects the nested structure
    return (dataArray as MvrApiDataItem[]).map((dataItem: MvrApiDataItem) => {
        const motorVehicleReport = dataItem?.motorVehicleReport;
        const requestSubject = motorVehicleReport?.requestSubject || {};
        const mvrReport = motorVehicleReport?.mvrReports?.[0] || {};
        const responseSubject = mvrReport?.responseSubject || {};
        const processingInfo = mvrReport?.processingInfo || {};
        const driversLicense = requestSubject.driversLicense || {};
        const name = requestSubject.name || {};

        // Find the correct driver license with expireDate, fallback to first available
        const driverLicenses = responseSubject.driversLicenses || [];
        const correctLicense = driverLicenses.find((license: MvrApiDriversLicense) => license.expireDate) || driverLicenses[0] || {};

        return {
            orderId: motorVehicleReport?.responseDetails?.orderId,
            status: motorVehicleReport?.responseDetails?.status,
            dateTimeOrdered: motorVehicleReport?.responseDetails?.dateTimeOrdered,
            dateTimeCompleted: motorVehicleReport?.responseDetails?.dateTimeCompleted,
            productProcessingStatusDescription: processingInfo.productProcessingStatusDescription,
            licenceStatus: correctLicense.licenseStatus || driversLicense.licenseStatus,
            driverInfo: {
                firstName: name.first || '',
                lastName: name.last || '',
                middleInitial: name.middle || '',
                licenseNumber: driversLicense.licenseNumber || '',
                licenseState: driversLicense.licenseState || '',
                dob: requestSubject.dob || '',
                gender: responseSubject.gender || undefined,
                dlClass: correctLicense.licenseClass || driversLicense.licenseClass || undefined,
                licenceStatus: correctLicense.licenseStatus || driversLicense.licenseStatus || undefined
            },
            mvrActivity: {
                violations: mvrReport.driverHistoryRecords?.filter((record: MvrApiDriverHistoryRecord) => record.recordType === 'Violation')
                    .map((record: MvrApiDriverHistoryRecord) => normalizeRecordDates(record) as Violation) || [],
                accidents: mvrReport.driverHistoryRecords?.filter((record: MvrApiDriverHistoryRecord) => record.recordType === 'Accident')
                    .map((record: MvrApiDriverHistoryRecord) => normalizeRecordDates(record) as Accident) || [],
                actions: mvrReport.driverHistoryRecords?.filter((record: MvrApiDriverHistoryRecord) => 
                    record.recordType !== 'Violation' && record.recordType !== 'Accident')
                    .map((record: MvrApiDriverHistoryRecord) => normalizeRecordDates(record) as Action) || []
            },
            alertMessages: { code: '', message: '' } // No alert messages in this structure format
        };
    });
}

export function mapSingleMvrReport(motorVehicleReport: MvrApiMotorVehicleReport): OrderMvrResponseBody {
    const requestSubject = motorVehicleReport.requestSubject || {};
    const mvrReport = motorVehicleReport.mvrReports?.[0] || {};
    const responseSubject = mvrReport.responseSubject || {};
    const processingInfo = mvrReport.processingInfo || {};
    const driversLicense = requestSubject.driversLicense || {};
    const name = requestSubject.name || {};

    // Find the correct driver license with expireDate, fallback to first available
    const driverLicenses = responseSubject.driversLicenses || [];
    const correctLicense = driverLicenses.find((license: MvrApiDriversLicense) => license.expireDate) || driverLicenses[0] || {};

    return {
        orderId: motorVehicleReport.responseDetails?.orderId,
        status: motorVehicleReport.responseDetails?.status,
        dateTimeOrdered: motorVehicleReport.responseDetails?.dateTimeOrdered,
        dateTimeCompleted: motorVehicleReport.responseDetails?.dateTimeCompleted,
        productProcessingStatusDescription: processingInfo.productProcessingStatusDescription,
        licenceStatus: correctLicense.licenseStatus || driversLicense.licenseStatus,
        driverInfo: {
            firstName: name.first || '',
            lastName: name.last || '',
            middleInitial: name.middle || '',
            licenseNumber: driversLicense.licenseNumber || '',
            licenseState: driversLicense.licenseState || '',
            dob: requestSubject.dob || '',
            gender: responseSubject.gender || undefined,
            dlClass: correctLicense.licenseClass || driversLicense.licenseClass || undefined,
            licenceStatus: correctLicense.licenseStatus || driversLicense.licenseStatus || undefined
        },
        mvrActivity: {
            violations: mvrReport.driverHistoryRecords?.filter((record: MvrApiDriverHistoryRecord) => record.recordType === 'Violation')
                .map((record: MvrApiDriverHistoryRecord) => normalizeRecordDates(record) as Violation) || [],
            accidents: mvrReport.driverHistoryRecords?.filter((record: MvrApiDriverHistoryRecord) => record.recordType === 'Accident')
                .map((record: MvrApiDriverHistoryRecord) => normalizeRecordDates(record) as Accident) || [],
            actions: mvrReport.driverHistoryRecords?.filter((record: MvrApiDriverHistoryRecord) => 
                record.recordType !== 'Violation' && record.recordType !== 'Accident')
                .map((record: MvrApiDriverHistoryRecord) => normalizeRecordDates(record) as Action) || []
        },
        alertMessages: { code: '', message: '' }
    };
}
